import { Button, LinearProgress, Typography, Box } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Survey } from "survey-react-ui";
import { useStore } from "../store/useStore";
import { routes } from "../routes/routes";
import { useUserStore } from "../store/useUserStore";
import { useEffect, useState } from "react";
import { useMatrixSurveyTooltips } from "../hooks/useMatrixSurveyTooltips";

import useFetchPhaseQuestionnaires from "../hooks/useFetchPhaseQuestionnaires";
import useAssignappAdvancePhase from "../hooks/useAssignappAdvancePhase";
import usePhaseQuestionnaireNavigation from "../hooks/usePhaseQuestionnaireNavigation";

import { ErrorSnackbar } from "../components/Snackbar/ErrorSnackbar";
import CustomSurveyNav from "../components/CustomSurveyNav";
import ConfirmationModal from "../components/Modal/ConfirmationModal";

const PhaseQuestionnairesPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const applicationId = searchParams.get("id");
  const phase = searchParams.get("phase") || "post";

  const userId = useUserStore((state) => state.userInfo?.id);
  const setAssignappId = useStore((state) => state.setAssignappId);

  const [openFinishPhaseModal, setOpenFinishPhaseModal] = useState(false);

  const { advancePhase } = useAssignappAdvancePhase();
  const { data, error, loading } = useFetchPhaseQuestionnaires(applicationId, userId, phase);

  const questionnaires = data?.questionnaires ?? [];
  const assignappId = data?.assignapp_id;

  const {
    currentQuestionnaireIndex,
    totalQuestionnaires,
    surveyModel,
    canRenderSurvey,
    responseLoading,
    showError,
    errorMessage,
    setShowError,
    isQuestionnaireComplete,
    progressValue,
    isLastQuestionnaire,
    handleFinishQuestionnaire,
  } = usePhaseQuestionnaireNavigation({
    questionnaires,
    assignappId,
    applicationId,
    phase,
    advancePhase,
    navigate,
    routes,
  });

  useMatrixSurveyTooltips(surveyModel);

  useEffect(() => {
    if (assignappId) {
      setAssignappId(assignappId);
    }
  }, [assignappId, setAssignappId]);

  const phaseTexts = {
    pre: {
      pageTitle: "Cuestionarios previos",
      finishButton: "Terminar cuestionarios previos",
      modalTitle: "¿Está seguro de que desea terminar los cuestionarios previos?",
      modalDescription: "Si termina esta fase, no podrá volver a abrir ni modificar los cuestionarios previos. ¿Desea continuar?",
    },
    post: {
      pageTitle: "Cuestionarios posteriores",
      finishButton: "Terminar cuestionarios posteriores",
      modalTitle: "¿Está seguro de que desea terminar los cuestionarios posteriores?",
      modalDescription: "Si termina esta fase, no podrá volver a abrir ni modificar los cuestionarios posteriores. ¿Desea continuar?",
    },
  };

  const currentPhaseTexts = phaseTexts[phase] || {
    pageTitle: "Cuestionarios de fase",
    finishButton: "Terminar",
    modalTitle: "¿Está seguro de que desea terminar esta fase?",
    modalDescription: "Si termina esta fase, no podrá volver a abrirla ni modificarla. ¿Desea continuar?",
  };

  const navFinishLabel = !isLastQuestionnaire ? "Siguiente Cuestionario" : currentPhaseTexts.finishButton;

  const handleRequestFinish = async () => {
    if (!isLastQuestionnaire) {
      await handleFinishQuestionnaire();
      return;
    }

    setOpenFinishPhaseModal(true);
  };

  const handleCloseFinishPhaseModal = () => {
    setOpenFinishPhaseModal(false);
  };

  const handleConfirmFinishPhase = async () => {
    setOpenFinishPhaseModal(false);
    await handleFinishQuestionnaire();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <CircularProgress />
      </div>
    );
  }

  if (error || !assignappId || questionnaires.length === 0) {
    return (
      <div className="w-full flex flex-col items-center justify-center h-full">
        <Typography variant="h6" sx={{ mb: 2 }}>
          {error?.message || "No hay cuestionarios disponibles"}
        </Typography>
        <Button variant="contained" onClick={() => navigate(routes.root, { replace: true })}>
          Volver al inicio
        </Button>
      </div>
    );
  }

  if (!canRenderSurvey || !surveyModel) {
    return (
      <>
        <div className="w-full flex flex-col items-center justify-center h-full">
          {responseLoading ? (
            <>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Verificando respuestas guardadas...
              </Typography>
            </>
          ) : (
            <Typography variant="body2" sx={{ mt: 2 }}>
              No se pudo validar las respuestas de los cuestionarios. Intente recargar la página.
            </Typography>
          )}
        </div>

        <ErrorSnackbar open={showError} onClose={() => setShowError(false)} title="Error" message={errorMessage} />
      </>
    );
  }

  return (
    <>
      <div className="questionnaire-view w-full justify-center items-center h-full flex flex-col">
        <div className="w-full mb-4">
          <Typography variant="h6" className="mb-2" sx={{ color: "#4a4a4a" }}>
            {currentPhaseTexts.pageTitle}
          </Typography>

          <Typography variant="body2" sx={{ mb: 1 }}>
            Cuestionario {currentQuestionnaireIndex + 1} de {totalQuestionnaires}
          </Typography>

          <LinearProgress variant="determinate" value={progressValue} />
        </div>

        <Box sx={{ width: "100%", height: "max-content", "& .sd-root-modern": { overflowY: "hidden" } }}>
          <Box sx={{ mb: 3 }}>
            <CustomSurveyNav
              model={surveyModel}
              onFinish={handleRequestFinish}
              disableFinish={!isQuestionnaireComplete}
              finishLabel={navFinishLabel}
            />
          </Box>

          <Survey model={surveyModel} />

          <CustomSurveyNav model={surveyModel} onFinish={handleRequestFinish} disableFinish={!isQuestionnaireComplete} finishLabel={navFinishLabel} />
        </Box>

        {!isQuestionnaireComplete && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Debe completar todas las preguntas antes de continuar.
          </Typography>
        )}
      </div>

      <ConfirmationModal
        open={openFinishPhaseModal}
        handleClose={handleCloseFinishPhaseModal}
        handleConfirm={handleConfirmFinishPhase}
        title={currentPhaseTexts.modalTitle}
        description={currentPhaseTexts.modalDescription}
        confirmText="Sí, terminar"
        cancelText="Cancelar"
        confirmColor="error"
      />

      <ErrorSnackbar open={showError} onClose={() => setShowError(false)} title="Error" message={errorMessage} />
    </>
  );
};

export default PhaseQuestionnairesPage;
